import { useState, useEffect } from 'react'
import {
    Layout, Eye, Save, GripVertical, ChevronDown, ChevronUp,
    Tag, TrendingUp, Mail, Megaphone, MessageCircle, Share2,
    ToggleLeft, ToggleRight, Check
} from 'lucide-react'

interface SidebarSection {
    id: string
    type: 'related' | 'categories' | 'cta' | 'newsletter' | 'popular' | 'ads'
    enabled: boolean
    title_vi: string
    title_en: string
}

interface LayoutConfig {
    layout_style: 'classic' | 'modern' | 'magazine' | 'minimal'
    hero_style: 'overlay' | 'above' | 'minimal'
    sidebar_config: { sections: SidebarSection[] }
    show_comments: boolean
    show_share: boolean
}

const layoutStyles = [
    {
        id: 'classic',
        name: 'Classic',
        desc: 'Hero nhỏ + Sidebar phải',
        preview: '📖'
    },
    {
        id: 'modern',
        name: 'Modern',
        desc: 'Hero full-width overlay + Sidebar',
        preview: '✨'
    },
    {
        id: 'magazine',
        name: 'Magazine',
        desc: 'Hero full-width + Không sidebar',
        preview: '📰'
    },
    {
        id: 'minimal',
        name: 'Minimal',
        desc: 'Tập trung nội dung, width hẹp',
        preview: '📝'
    }
]

const heroStyles = [
    { id: 'overlay', name: 'Overlay', desc: 'Nội dung đè lên ảnh' },
    { id: 'above', name: 'Above', desc: 'Nội dung dưới ảnh' },
    { id: 'minimal', name: 'Minimal', desc: 'Không hero, ảnh trong nội dung' }
]

const sectionTypes = [
    { type: 'related', icon: Tag, name: 'Bài liên quan', color: 'text-blue-500' },
    { type: 'categories', icon: TrendingUp, name: 'Danh mục', color: 'text-green-500' },
    { type: 'cta', icon: Megaphone, name: 'Call to Action', color: 'text-orange-500' },
    { type: 'newsletter', icon: Mail, name: 'Newsletter', color: 'text-purple-500' }
]

export default function ArticleLayoutBuilder() {
    const [config, setConfig] = useState<LayoutConfig | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

    useEffect(() => {
        fetchConfig()
    }, [])

    const fetchConfig = async () => {
        try {
            const res = await fetch('/api/admin/article-layout')
            const data = await res.json()
            if (data.success) {
                setConfig(data.config)
            }
        } catch (error) {
            console.error('Failed to fetch config:', error)
        } finally {
            setLoading(false)
        }
    }

    const saveConfig = async () => {
        if (!config) return
        setSaving(true)
        try {
            const res = await fetch('/api/admin/article-layout', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            })
            const data = await res.json()
            if (data.success) {
                setSaved(true)
                setTimeout(() => setSaved(false), 2000)
            }
        } catch (error) {
            console.error('Failed to save config:', error)
        } finally {
            setSaving(false)
        }
    }

    const updateConfig = (key: keyof LayoutConfig, value: any) => {
        if (!config) return
        setConfig({ ...config, [key]: value })
    }

    const toggleSection = (index: number) => {
        if (!config) return
        const sections = [...config.sidebar_config.sections]
        sections[index].enabled = !sections[index].enabled
        setConfig({
            ...config,
            sidebar_config: { sections }
        })
    }

    const moveSection = (index: number, direction: 'up' | 'down') => {
        if (!config) return
        const sections = [...config.sidebar_config.sections]
        const newIndex = direction === 'up' ? index - 1 : index + 1
        if (newIndex < 0 || newIndex >= sections.length) return

        [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]]
        setConfig({
            ...config,
            sidebar_config: { sections }
        })
    }

    const handleDragStart = (index: number) => {
        setDraggedIndex(index)
    }

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault()
        if (draggedIndex === null || draggedIndex === index) return

        if (!config) return
        const sections = [...config.sidebar_config.sections]
        const draggedSection = sections[draggedIndex]
        sections.splice(draggedIndex, 1)
        sections.splice(index, 0, draggedSection)

        setConfig({
            ...config,
            sidebar_config: { sections }
        })
        setDraggedIndex(index)
    }

    const handleDragEnd = () => {
        setDraggedIndex(null)
    }

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-gray-200 rounded w-1/3" />
                    <div className="h-64 bg-gray-200 rounded" />
                </div>
            </div>
        )
    }

    if (!config) {
        return (
            <div className="p-6 text-center text-red-500">
                Failed to load configuration
            </div>
        )
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Layout className="w-6 h-6 text-primary-600" />
                        Article Layout Builder
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Tùy chỉnh giao diện trang bài viết
                    </p>
                </div>
                <button
                    onClick={saveConfig}
                    disabled={saving}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition ${saved
                        ? 'bg-green-600 text-white'
                        : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                >
                    {saved ? <Check className="w-5 h-5" /> : <Save className="w-5 h-5" />}
                    {saving ? 'Đang lưu...' : saved ? 'Đã lưu!' : 'Lưu thay đổi'}
                </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Left Column - Settings */}
                <div className="space-y-6">
                    {/* Layout Style */}
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        <h2 className="font-bold text-lg mb-4">Layout Style</h2>
                        <div className="grid grid-cols-2 gap-3">
                            {layoutStyles.map(style => (
                                <button
                                    key={style.id}
                                    onClick={() => updateConfig('layout_style', style.id)}
                                    className={`p-4 rounded-xl border-2 text-left transition ${config.layout_style === style.id
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="text-3xl mb-2">{style.preview}</div>
                                    <div className="font-semibold">{style.name}</div>
                                    <div className="text-xs text-gray-500">{style.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Hero Style */}
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        <h2 className="font-bold text-lg mb-4">Hero Style</h2>
                        <div className="space-y-2">
                            {heroStyles.map(style => (
                                <button
                                    key={style.id}
                                    onClick={() => updateConfig('hero_style', style.id)}
                                    className={`w-full p-3 rounded-lg border-2 text-left transition flex items-center justify-between ${config.hero_style === style.id
                                        ? 'border-primary-500 bg-primary-50'
                                        : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <div>
                                        <div className="font-medium">{style.name}</div>
                                        <div className="text-xs text-gray-500">{style.desc}</div>
                                    </div>
                                    {config.hero_style === style.id && (
                                        <Check className="w-5 h-5 text-primary-600" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Toggle Options */}
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        <h2 className="font-bold text-lg mb-4">Tùy chọn khác</h2>
                        <div className="space-y-4">
                            <button
                                onClick={() => updateConfig('show_share', !config.show_share)}
                                className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-3">
                                    <Share2 className="w-5 h-5 text-gray-500" />
                                    <span>Hiển thị nút chia sẻ</span>
                                </div>
                                {config.show_share ? (
                                    <ToggleRight className="w-8 h-8 text-primary-600" />
                                ) : (
                                    <ToggleLeft className="w-8 h-8 text-gray-400" />
                                )}
                            </button>
                            <button
                                onClick={() => updateConfig('show_comments', !config.show_comments)}
                                className="w-full flex items-center justify-between p-3 rounded-lg border hover:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-3">
                                    <MessageCircle className="w-5 h-5 text-gray-500" />
                                    <span>Hiển thị phần bình luận</span>
                                </div>
                                {config.show_comments ? (
                                    <ToggleRight className="w-8 h-8 text-primary-600" />
                                ) : (
                                    <ToggleLeft className="w-8 h-8 text-gray-400" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Sidebar Sections - Drag & Drop */}
                    <div className="bg-white rounded-xl p-5 border shadow-sm">
                        <h2 className="font-bold text-lg mb-4">Sidebar Sections</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Kéo thả để sắp xếp thứ tự, bật/tắt để hiển thị
                        </p>
                        <div className="space-y-2">
                            {config.sidebar_config.sections.map((section, index) => {
                                const sectionInfo = sectionTypes.find(s => s.type === section.type)
                                const Icon = sectionInfo?.icon || Tag

                                return (
                                    <div
                                        key={section.id}
                                        draggable
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={(e) => handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                        className={`flex items-center gap-3 p-3 rounded-lg border transition ${draggedIndex === index
                                            ? 'bg-primary-50 border-primary-300'
                                            : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                    >
                                        <GripVertical className="w-5 h-5 text-gray-400 cursor-grab" />
                                        <Icon className={`w-5 h-5 ${sectionInfo?.color || 'text-gray-500'}`} />
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">{section.title_vi}</div>
                                            <div className="text-xs text-gray-500">{sectionInfo?.name}</div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button
                                                onClick={() => moveSection(index, 'up')}
                                                disabled={index === 0}
                                                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                            >
                                                <ChevronUp className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => moveSection(index, 'down')}
                                                disabled={index === config.sidebar_config.sections.length - 1}
                                                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                            >
                                                <ChevronDown className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => toggleSection(index)}
                                            className="ml-2"
                                        >
                                            {section.enabled ? (
                                                <ToggleRight className="w-8 h-8 text-primary-600" />
                                            ) : (
                                                <ToggleLeft className="w-8 h-8 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Column - Live Preview */}
                <div className="lg:sticky lg:top-6 lg:self-start">
                    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
                        <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
                            <Eye className="w-5 h-5 text-gray-500" />
                            <span className="font-medium">Live Preview</span>
                        </div>

                        {/* Mini Preview */}
                        <div className="p-4">
                            <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ aspectRatio: '4/3' }}>
                                {/* Hero Preview */}
                                <div className={`relative ${config.hero_style === 'minimal' ? 'h-8 bg-gray-200' : 'h-24 bg-gradient-to-r from-primary-400 to-orange-400'
                                    }`}>
                                    {config.hero_style === 'overlay' && (
                                        <div className="absolute bottom-2 left-3 right-3">
                                            <div className="h-2 w-1/3 bg-white/80 rounded mb-1" />
                                            <div className="h-3 w-2/3 bg-white/90 rounded" />
                                        </div>
                                    )}
                                </div>

                                {/* Content Preview */}
                                <div className={`p-3 bg-white flex gap-3 ${config.layout_style === 'magazine' || config.layout_style === 'minimal'
                                    ? 'justify-center' : ''
                                    }`}>
                                    {/* Main Content */}
                                    <div className={`space-y-2 ${config.layout_style === 'magazine' ? 'w-full max-w-[70%]' :
                                        config.layout_style === 'minimal' ? 'w-full max-w-[60%]' :
                                            'flex-1'
                                        }`}>
                                        {config.hero_style === 'above' && (
                                            <>
                                                <div className="h-2 w-1/4 bg-gray-300 rounded" />
                                                <div className="h-3 w-3/4 bg-gray-400 rounded" />
                                            </>
                                        )}
                                        <div className="h-2 bg-gray-200 rounded" />
                                        <div className="h-2 bg-gray-200 rounded w-5/6" />
                                        <div className="h-2 bg-gray-200 rounded w-4/6" />
                                        <div className="h-2 bg-gray-200 rounded" />

                                        {config.show_share && (
                                            <div className="flex gap-1 pt-1">
                                                <div className="h-4 w-8 bg-blue-200 rounded" />
                                                <div className="h-4 w-8 bg-sky-200 rounded" />
                                                <div className="h-4 w-8 bg-gray-200 rounded" />
                                            </div>
                                        )}

                                        {config.show_comments && (
                                            <div className="h-8 bg-gray-100 rounded mt-2" />
                                        )}
                                    </div>

                                    {/* Sidebar Preview */}
                                    {config.layout_style !== 'magazine' && config.layout_style !== 'minimal' && (
                                        <div className="w-20 space-y-2">
                                            {config.sidebar_config.sections
                                                .filter(s => s.enabled)
                                                .slice(0, 3)
                                                .map((section) => (
                                                    <div
                                                        key={section.id}
                                                        className={`h-6 rounded ${section.type === 'cta'
                                                            ? 'bg-gradient-to-r from-primary-200 to-orange-200'
                                                            : 'bg-gray-200'
                                                            }`}
                                                    />
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Preview Info */}
                            <div className="mt-4 text-center text-sm text-gray-500">
                                <span className="font-medium">{layoutStyles.find(s => s.id === config.layout_style)?.name}</span>
                                {' • '}
                                <span>{heroStyles.find(s => s.id === config.hero_style)?.name} hero</span>
                                {' • '}
                                <span>{config.sidebar_config.sections.filter(s => s.enabled).length} sidebar sections</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
